'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams as useNextSearchParams } from 'next/navigation';

import type {
  SearchParamKeys,
  SearchParamValues,
  UseSearchParamsWithKeys,
  UseSearchParamsWithoutKeys,
} from './_types/use-search-params.type';

export function useSearchParams<T extends SearchParamKeys>(keys: T): UseSearchParamsWithKeys<T>;

export function useSearchParams(): UseSearchParamsWithoutKeys;

export function useSearchParams<T extends SearchParamKeys>(keys?: T) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useNextSearchParams();

  const selectedValues = useMemo(() => {
    if (!keys || keys.length === 0) {
      const allParams: Record<string, string | null> = {};
      searchParams?.forEach((value, key) => {
        allParams[key] = value;
      });
      return allParams;
    }

    return keys.reduce((acc, key) => {
      acc[key as T[number]] = searchParams?.get(key) ?? null;
      return acc;
    }, {} as SearchParamValues<T>);
  }, [keys, searchParams]);

  const updateUrl = useCallback(
    (params: URLSearchParams) => {
      const query = params.toString();

      if (query === (searchParams?.toString() ?? '')) {
        return;
      }

      const href = query ? `${pathname}?${query}` : pathname;

      router.replace(href, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  const setParams = useCallback(
    (nextValues: Partial<SearchParamValues<T>> | Record<string, string | null | undefined>) => {
      const params = new URLSearchParams(searchParams?.toString());

      Object.entries(nextValues).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          params.delete(key);
          return;
        }

        params.set(key, String(value));
      });

      updateUrl(params);
    },
    [searchParams, updateUrl],
  );

  const removeParams = useCallback(
    (
      keysToDelete:
        | (T extends SearchParamKeys ? T[number] : string)
        | (T extends SearchParamKeys ? T[number] : string)[],
    ) => {
      const params = new URLSearchParams(searchParams?.toString());
      const keysArray = Array.isArray(keysToDelete) ? keysToDelete : [keysToDelete];

      keysArray.forEach((key) => params.delete(key));
      updateUrl(params);
    },
    [searchParams, updateUrl],
  );

  const removeParamValue = useCallback(
    (key: T extends SearchParamKeys ? T[number] : string, value: string) => {
      const params = new URLSearchParams(searchParams?.toString());
      const remainingValues = params.getAll(key).filter((paramValue) => paramValue !== value);

      params.delete(key);
      remainingValues.forEach((paramValue) => params.append(key, paramValue));

      updateUrl(params);
    },
    [searchParams, updateUrl],
  );

  const getParam = useCallback(
    (key: string): string | null => searchParams?.get(key) ?? null,
    [searchParams],
  );

  const getAll = useCallback(
    (key: string): string[] => searchParams?.getAll(key) ?? [],
    [searchParams],
  );

  const has = useCallback(
    (key: string): boolean => searchParams?.has(key) ?? false,
    [searchParams],
  );

  const appendParam = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams?.toString());
      params.append(key, value);
      updateUrl(params);
    },
    [searchParams, updateUrl],
  );

  const getAllParams = useCallback(() => {
    return new URLSearchParams(searchParams?.toString());
  }, [searchParams]);

  if (keys && keys.length > 0) {
    return {
      ...selectedValues,
      values: selectedValues,
      setParams,
      removeParams,
      removeParamValue,
      getParam,
      getAll,
      has,
      appendParam,
      getAllParams,
    };
  }

  return {
    values: selectedValues,
    setParams,
    removeParams,
    removeParamValue,
    getParam,
    getAll,
    has,
    appendParam,
    getAllParams,
  };
}
